/**
 * RemittanceDetailDialog and sub-components.
 *
 * Extracted from the original RemittancesTab.tsx — preserves all existing
 * detail-view functionality (claim accordions, service lines, adjustments, remarks).
 */

import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MODAL_TYPES } from '@components/modals/constants';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  OpenInNew as OpenInNewIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEventTracking } from '@services/statsig';
import type {
  RemittanceClaimResponse2,
  RemittanceLineResponse2,
} from '@store/generated/billing';
import { useModal } from '@store/hooks';

import {
  useEraDownload,
  useGetEncounterDetailQuery,
  useGetProviderRemittanceDetailQuery,
} from '../../../store';

// ============================================================================
// Types (for loosely-typed adjustment/remark fields)
// ============================================================================

interface AdjustmentEntry {
  groupCode?: string;
  reasonCode?: string;
  reasonDescription?: string;
  amountCents?: number;
}

interface RemarkCodeEntry {
  remarkCode?: string;
  remark?: string;
  codeListQualifierCode?: string;
}

// ============================================================================
// Constants
// ============================================================================

const CLAIM_STATUS_LABELS: Record<
  string,
  { label: string; color: 'success' | 'info' | 'error' | 'warning' | 'default' }
> = {
  '1': { label: 'Processed (Primary)', color: 'success' },
  '2': { label: 'Processed (Secondary)', color: 'info' },
  '4': { label: 'Denied', color: 'error' },
  '22': { label: 'Reversal', color: 'warning' },
};

const ADJUSTMENT_GROUP_LABELS: Record<string, string> = {
  CO: 'Contractual Write-off',
  PR: 'Patient Responsibility',
  OA: 'Other Adjustment',
  PI: 'Payor Initiated',
  CR: 'Corrections/Reversals',
};

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  CHK: 'Check',
  ACH: 'Electronic (ACH)',
};

// ============================================================================
// Helpers
// ============================================================================

function formatCents(cents: number | null | undefined): string {
  if (cents == null) return '\u2014';
  return `$${(cents / 100).toFixed(2)}`;
}

function getClaimStatusDisplay(
  code: string,
): { label: string; color: 'success' | 'info' | 'error' | 'warning' | 'default' } {
  return CLAIM_STATUS_LABELS[code] ?? { label: `Status ${code}`, color: 'default' };
}

function getPaymentMethodLabel(code: string | null | undefined): string {
  if (!code) return '\u2014';
  return PAYMENT_METHOD_LABELS[code] ?? code;
}

function getAdjustmentGroupLabel(code: string): string {
  return ADJUSTMENT_GROUP_LABELS[code] ?? code;
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '\u2014';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

// ============================================================================
// Sub-Components
// ============================================================================

function ServiceLineRows({ line }: { line: RemittanceLineResponse2 }) {
  const theme = useTheme();
  const adjustments = (line.service_adjustments ?? []) as AdjustmentEntry[];
  const remarks = (line.remark_codes ?? []) as RemarkCodeEntry[];
  const codeChanged =
    line.submitted_procedure_code && line.submitted_procedure_code !== line.adjudicated_procedure_code;

  return (
    <>
      <TableRow>
        <TableCell>
          <Box>
            <Typography
              variant="body2"
              sx={{ fontFamily: 'monospace', fontSize: '0.8125rem', fontWeight: 500 }}
            >
              {line.adjudicated_procedure_code ?? '\u2014'}
            </Typography>
            {codeChanged && (
              <Typography variant="caption" color="text.secondary">
                Submitted: {line.submitted_procedure_code}
              </Typography>
            )}
          </Box>
        </TableCell>
        <TableCell>
          {line.adjudicated_procedure_modifier_codes &&
          line.adjudicated_procedure_modifier_codes.length > 0 ? (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {line.adjudicated_procedure_modifier_codes.map((mod, i) => (
                <Chip key={i} label={mod} size="small" variant="outlined" />
              ))}
            </Box>
          ) : (
            '\u2014'
          )}
        </TableCell>
        <TableCell>{line.service_date ?? '\u2014'}</TableCell>
        <TableCell align="right">{formatCents(line.line_item_charge_amount_cents)}</TableCell>
        <TableCell align="right">{formatCents(line.line_item_payment_amount_cents)}</TableCell>
      </TableRow>

      {adjustments.length > 0 && (
        <TableRow>
          <TableCell
            colSpan={5}
            sx={{ py: 0.5, pl: 4, bgcolor: theme.palette.grey[50], borderBottom: 'none' }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
              {adjustments.map((adj, i) => (
                <Typography key={i} variant="caption" color="text.secondary">
                  <Chip
                    label={adj.groupCode ?? '?'}
                    size="small"
                    variant="outlined"
                    sx={{ mr: 0.5, height: 18, fontSize: '0.6875rem' }}
                  />
                  {adj.reasonCode} \u2014 {adj.reasonDescription ?? 'Unknown'}:{' '}
                  {formatCents(adj.amountCents)}
                </Typography>
              ))}
            </Box>
          </TableCell>
        </TableRow>
      )}

      {remarks.length > 0 && (
        <TableRow>
          <TableCell colSpan={5} sx={{ py: 0.5, pl: 4, bgcolor: theme.palette.grey[50] }}>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                Remarks:
              </Typography>
              {remarks.map((r, i) => (
                <Tooltip key={i} title={r.remark ?? ''}>
                  <Chip label={r.remarkCode ?? '?'} size="small" variant="outlined" />
                </Tooltip>
              ))}
            </Box>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function BillPatientButton({ claim }: { claim: RemittanceClaimResponse2 }) {
  const { open } = useModal();
  const { patientBilling } = useEventTracking();

  // Fetch patient_id from the linked encounter (only when claim is matched)
  const { data: encounterDetail } = useGetEncounterDetailQuery(
    { encounterId: claim.encounter_id ?? '' },
    { skip: !claim.encounter_id },
  );

  const hasPatientResponsibility =
    claim.patient_responsibility_amount_cents != null &&
    claim.patient_responsibility_amount_cents > 0;
  const isMatched = !!claim.claim_id;

  if (!hasPatientResponsibility || !isMatched) return null;

  const patientId = encounterDetail?.patient_id;
  const patientName = [claim.patient_first_name, claim.patient_last_name]
    .filter(Boolean)
    .join(' ') || undefined;

  // Build reason from remittance line CPT codes
  const cptCodes = claim.lines
    ?.map(l => l.adjudicated_procedure_code)
    .filter(Boolean)
    .join(', ');
  const reason = cptCodes
    ? `Patient responsibility — CPT ${cptCodes}`
    : 'Patient responsibility';

  const handleBillPatient = (e: React.MouseEvent) => {
    e.stopPropagation();

    patientBilling.billClicked(claim.remittance_claim_id, {
      amount_cents: claim.patient_responsibility_amount_cents ?? undefined,
    });

    open(MODAL_TYPES.PAYMENT_REQUEST, {
      patientId,
      patientName,
      amount: ((claim.patient_responsibility_amount_cents ?? 0) / 100).toFixed(2),
      reason,
      encounterId: claim.encounter_id,
      source: 'remittance_detail',
    });
  };

  return (
    <Button
      size="small"
      variant="outlined"
      startIcon={<ReceiptIcon sx={{ fontSize: 16 }} />}
      disabled={!patientId}
      onClick={handleBillPatient}
      sx={{ textTransform: 'none', fontSize: '0.75rem', whiteSpace: 'nowrap' }}
    >
      Bill Patient
    </Button>
  );
}

function ClaimAccordion({
  claim,
  isExpanded,
  onToggle,
}: {
  claim: RemittanceClaimResponse2;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const theme = useTheme();
  const navigate = useNavigate();
  const status = getClaimStatusDisplay(claim.claim_status_code);

  return (
    <Box sx={{ border: `1px solid ${theme.palette.grey[200]}`, borderRadius: 1.5 }}>
      <Box
        sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          '&:hover': { bgcolor: theme.palette.grey[50] },
        }}
        onClick={onToggle}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {claim.patient_first_name || claim.patient_last_name
                ? `${claim.patient_first_name ?? ''} ${claim.patient_last_name ?? ''}`.trim()
                : `PCN: ${claim.patient_control_number}`}
            </Typography>
            {claim.claim_id ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontFamily: 'monospace' }}
              >
                Claim: {claim.claim_id}
              </Typography>
            ) : (
              <Chip label="Unmatched" size="small" variant="outlined" color="warning" />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {claim.patient_member_id && (
              <Typography variant="caption" color="text.secondary">
                Member ID: {claim.patient_member_id}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary">
              Billed: {formatCents(claim.total_claim_charge_amount_cents)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Paid: {formatCents(claim.claim_payment_amount_cents)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Patient: {formatCents(claim.patient_responsibility_amount_cents)}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
          <BillPatientButton claim={claim} />
          <Chip label={status.label} size="small" color={status.color} />
          {claim.forwarded_to_secondary && (
            <Chip label="Fwd to Secondary" size="small" variant="outlined" color="info" />
          )}
          <ExpandMoreIcon
            sx={{
              transform: isExpanded ? 'rotate(180deg)' : 'none',
              transition: '0.2s',
              color: theme.palette.text.secondary,
            }}
          />
        </Box>
      </Box>

      <Collapse in={isExpanded}>
        <Divider />
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {claim.payer_claim_control_number && (
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">
                  Payer Claim Ref
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}
                >
                  {claim.payer_claim_control_number}
                </Typography>
              </Grid>
            )}
            {claim.rendering_provider_npi && (
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">
                  Rendering NPI
                </Typography>
                <Typography variant="body2">{claim.rendering_provider_npi}</Typography>
              </Grid>
            )}
            {(claim.statement_period_start || claim.statement_period_end) && (
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">
                  Statement Period
                </Typography>
                <Typography variant="body2">
                  {claim.statement_period_start ?? '\u2014'} \u2013 {claim.statement_period_end ?? '\u2014'}
                </Typography>
              </Grid>
            )}
            {claim.claim_received_date && (
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">
                  Received
                </Typography>
                <Typography variant="body2">{claim.claim_received_date}</Typography>
              </Grid>
            )}
          </Grid>

          {claim.claim_adjustments && claim.claim_adjustments.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Claim Adjustments
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Group</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {claim.claim_adjustments.map((adj, i) => {
                    const a = adj as AdjustmentEntry;
                    return (
                      <TableRow key={i}>
                        <TableCell>
                          <Chip
                            label={a.groupCode ?? '\u2014'}
                            size="small"
                            variant="outlined"
                            title={
                              a.groupCode ? getAdjustmentGroupLabel(a.groupCode) : undefined
                            }
                          />
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>
                          {a.reasonCode ?? '\u2014'}
                        </TableCell>
                        <TableCell>{a.reasonDescription ?? '\u2014'}</TableCell>
                        <TableCell align="right">{formatCents(a.amountCents)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}

          {claim.lines && claim.lines.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Service Lines
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Procedure</TableCell>
                      <TableCell>Modifiers</TableCell>
                      <TableCell>Service Date</TableCell>
                      <TableCell align="right">Billed</TableCell>
                      <TableCell align="right">Paid</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {claim.lines.map(line => (
                      <ServiceLineRows key={line.remittance_line_id} line={line} />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {claim.encounter_id && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
                onClick={() => navigate(`/billing/encounter/${claim.encounter_id}`)}
                sx={{ textTransform: 'none', fontSize: '0.75rem' }}
              >
                View Encounter
              </Button>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

// ============================================================================
// Detail Dialog
// ============================================================================

export function RemittanceDetailDialog({
  open,
  onClose,
  remittanceId,
}: {
  open: boolean;
  onClose: () => void;
  remittanceId: string | null;
}) {
  const theme = useTheme();
  const [expandedClaimId, setExpandedClaimId] = useState<string | null>(null);

  const { data, isLoading, error } = useGetProviderRemittanceDetailQuery(
    { remittanceId: remittanceId ?? '' },
    { skip: !remittanceId || !open },
  );

  const { downloadEra, isDownloading: isDownloadingEra } = useEraDownload();

  const remittance = data?.remittance;
  const claims = data?.claims ?? [];

  const isStediSourced = remittance?.source_system === 'stedi';

  const handleDownloadEra = useCallback(() => {
    if (remittanceId) {
      downloadEra(remittanceId);
    }
  }, [remittanceId, downloadEra]);

  const handleToggleClaim = useCallback((claimId: string) => {
    setExpandedClaimId(prev => (prev === claimId ? null : claimId));
  }, []);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Payment Detail</span>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {isStediSourced && (
              <IconButton
                onClick={handleDownloadEra}
                disabled={isDownloadingEra}
                size="small"
                aria-label="Download ERA file"
              >
                {isDownloadingEra ? <CircularProgress size={18} /> : <DownloadIcon />}
              </IconButton>
            )}
            <IconButton edge="end" onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error">Failed to load payment details. Please try again.</Alert>
        )}

        {remittance && (
          <Box>
            {/* Header Summary */}
            <Box
              sx={{
                p: 2.5,
                border: `1px solid ${theme.palette.grey[200]}`,
                borderRadius: 2,
                bgcolor: theme.palette.grey[50],
                mb: 3,
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontFamily: 'monospace', fontSize: '0.875rem', fontWeight: 600, mb: 0.5 }}
              >
                {remittance.check_or_eft_trace_number}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Typography variant="body2" color="text.secondary">
                  {remittance.payer_name ?? 'Unknown Payer'}
                </Typography>
                <Chip
                  label={getPaymentMethodLabel(remittance.payment_method_code)}
                  size="small"
                  variant="outlined"
                />
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Total Paid
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatCents(
                      remittance.total_payment_amount_cents ??
                        (claims.reduce(
                          (sum, c) => sum + (c.claim_payment_amount_cents ?? 0),
                          0,
                        ) || null),
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Claims
                  </Typography>
                  <Typography variant="body2">{claims.length}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Payment Date
                  </Typography>
                  <Typography variant="body2">{formatDate(remittance.payment_date)}</Typography>
                </Grid>
              </Grid>
            </Box>

            {/* Claims */}
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
              Claims ({claims.length})
            </Typography>

            {claims.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {claims.map(claim => (
                  <ClaimAccordion
                    key={claim.remittance_claim_id}
                    claim={claim}
                    isExpanded={expandedClaimId === claim.remittance_claim_id}
                    onToggle={() => handleToggleClaim(claim.remittance_claim_id)}
                  />
                ))}
              </Box>
            ) : (
              <Alert severity="info">
                <Typography variant="body2">
                  No claim details available for this payment.
                </Typography>
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
